import { signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { WidgetState } from './widget.state';
import { inject } from '@angular/core';
import { IndicatorStore } from '../indicator/indicator.state';
import { DataLayerStore } from '../data-layer/data-layer.state';
import { IndicatorDataStore } from '../indicatpr-data/indicator-data.state';
import { WidgetHelperService } from '../../services/widget/widget-helper.service';
import { DataGroupProperty } from '../indicatpr-data/indicator-data.widget-selectors';
import { EntityId } from '@ngrx/signals/entities';
import { withWidgetCoreFeatures } from './widget.core-features';
import { makeUniqueStringArray } from '../../utility/array.utility';
import { ChartDataset } from 'chart.js';
import {
  CustomGridStackWidget,
  RegressionData,
  ScatterData,
  WegweiserData,
  WegweiserIndicator,
  WegweiserRegion,
  WidgetConfig,
  WidgetType,
} from '../../services/grid-widget-config.service';
import { ComparisonValueStore } from '../comparison-value/comparison-value.state';
import { ComparisonValue } from '../../models/geo-data';
import { updateAlpha } from '../../utility/color.utility';
import { DataLayer } from '../../models/data-layer';
import { GridStore } from './grid.state';

/**
 * Computes the minimum value from an array of datasets, where each dataset contains a label and an array of numeric data.
 *
 * @param {Array<{ label: string, data: number[] }>} datasets - The array of datasets containing labels and numeric data values.
 * @return {number} The minimum numeric value found across all datasets.
 */
function getMinValue(datasets: { label: string; data: number[] }[]): number {
  return Math.min(...datasets.flatMap(dataset => dataset.data));
}

/**
 * Finds and returns the maximum value from an array of datasets.
 *
 * @param {Array<{ label: string, data: number[] }>} datasets - An array of objects, each containing a label and an array of numeric data.
 * @return {number} The maximum value found across all the dataset arrays.
 */
function getMaxValue(datasets: { label: string; data: number[] }[]): number {
  return Math.max(...datasets.flatMap(dataset => dataset.data));
}

/**
 * Sorts Chart.js datasets based on a given criteria.
 *
 * @param datasets - Array of Chart.js datasets.
 * @param sortBy - Criteria to sort by ('label' | 'value').
 * @param direction - Sort direction ('asc' | 'desc').
 * @returns Sorted array of datasets.
 */
function sortChartDatasets(
  datasets: { label: string; data: number[] }[],
  sortBy: 'label' | 'value',
  direction: 'asc' | 'desc'
): { label: string; data: number[] }[] {
  return datasets.sort((a, b) => {
    let compareA: string | number;
    let compareB: string | number;

    if (sortBy === 'label') {
      compareA = a.label.toLowerCase();
      compareB = b.label.toLowerCase();
    } else if (sortBy === 'value') {
      // Assuming sort by the first value in the data array
      compareA = a.data.reduce((sum, val) => sum + val, 0); // Adjust to single or total value
      compareB = b.data.reduce((sum, val) => sum + val, 0); // Adjust to single or total value
    } else {
      throw new Error('Invalid sortBy criteria. Use "label" or "value".');
    }

    if (direction === 'asc') {
      return compareA < compareB ? -1 : compareA > compareB ? 1 : 0;
    } else {
      return compareA > compareB ? -1 : compareA < compareB ? 1 : 0;
    }
  });
}

/**
 * Sorts Chart.js bar chart data groups (x-axis labels and corresponding data) based on a given criteria.
 *
 * @param labels - Array of x-axis labels.
 * @param keys - Array of x-axis keys.
 * @param datasets - Array of Chart.js datasets.
 * @param enableSorting - Enable sort for the chart
 * @param sortBy - Criteria to sort by ('label' | 'value').
 * @param direction - Sort direction ('asc' | 'desc').
 * @returns An object with sorted labels and datasets.
 */
function sortChartGroups(
  labels: string[],
  keys: string[],
  datasets: { label: string; data: number[] }[],
  enableSorting: boolean,
  sortBy?: 'label' | 'value',
  direction?: 'asc' | 'desc'
): {
  labels: string[];
  datasets: { label: string; data: number[] }[];
  groups: any[];
} {
  // Create an array of objects representing groups
  const groups = labels.map((label, index) => ({
    label: String(label),
    key: String(keys[index]),
    values: datasets.map(dataset => dataset.data[index]),
  }));

  if (!sortBy || !direction || !enableSorting)
    return { labels: labels, datasets: datasets, groups: groups }; // TODO - kein sortieren sollte möglich sein!!!
  // Sort groups based on the specified criteria

  groups.sort((a, b) => {
    let compareA: string | number;
    let compareB: string | number;

    if (sortBy === 'label') {
      compareA = a.label.toLowerCase();
      compareB = b.label.toLowerCase();
    } else if (sortBy === 'value') {
      compareA = a.values.reduce((sum, val) => sum + Number(val), 0); // Sum of values for sort
      compareB = b.values.reduce((sum, val) => sum + Number(val), 0);
    } else {
      throw new Error('Invalid sortBy criteria. Use "label" or "value".');
    }

    if (direction === 'asc') {
      return compareA < compareB ? -1 : compareA > compareB ? 1 : 0;
    } else {
      return compareA > compareB ? -1 : compareA < compareB ? 1 : 0;
    }
  });

  // Extract sorted labels and datasets
  const sortedLabels = groups.map(group => group.label);
  const sortedDatasets = datasets.map((dataset, datasetIndex) => ({
    ...dataset,
    data: groups.map(group => group.values[datasetIndex]),
    dataKeys: groups.map(group => group.key),
  }));

  return { labels: sortedLabels, datasets: sortedDatasets, groups: groups };
}

export function withChartMethods() {
  return signalStoreFeature(
    {
      state: type<WidgetState>(),
    },
    withWidgetCoreFeatures(),
    withMethods(
      (
        { activeWidget, widgetType, selected, update, entityMap, get },
        indicatorStore = inject(IndicatorStore),
        layerStore = inject(DataLayerStore),
        indicatorDataStore = inject(IndicatorDataStore),
        gridStore = inject(GridStore),
        comparisonStore = inject(ComparisonValueStore),
        helper = inject(WidgetHelperService)
      ) => {
        const chartMethods = {
          getLabelsByGroupProperty(
            config: WidgetConfig,
            dataLayerIds: string[],
            dataKeys: string[]
          ): string[] | number[] {
            if (!config) return [];

            switch (config.groupProperty) {
              case 'indicator':
                return indicatorStore
                  .getByIds(config.indicators)
                  .map(indicator =>
                    config.showFullIndicatorName
                      ? indicatorStore.getFullName(indicator)
                      : indicator.name
                  );
              case 'feature':
                // eslint-disable-next-line no-case-declarations
                const features: any = {
                  ...config.features,
                  ...comparisonStore.byKey(),
                };
                return dataKeys.map(key => features[key].name);
              default: // dataLayer
                return layerStore
                  .getByIds(dataLayerIds)
                  .map((layer: DataLayer) => Number(layer.year))
                  .filter(year => !config.disabledYears.includes(year));
            }
          },
          getKeysByGroupProperty(config: WidgetConfig) {
            if (!config) return [];

            switch (config.groupProperty) {
              case 'indicator':
                return indicatorStore
                  .getByIds(config.indicators)
                  .map(indicator => indicator._id);
              //.map(indicator => indicator.key);
              case 'feature':
                // eslint-disable-next-line no-case-declarations
                let features = [
                  ...Object.values(config.features || {}),
                  ...comparisonStore.asArray(),
                ];
                if (config.disabledFeatures) {
                  features = features.filter(
                    item => !config.disabledFeatures?.includes(item.key)
                  );
                }
                return features.map(district => district.key);
              default:
                return layerStore
                  .getYearsByParent(config.dataLayerType)
                  .filter(year => !config.disabledYears.includes(year));
            }
          },

          getDatasetLabel(
            widget: WidgetConfig,
            property: string,
            key: string,
            indicatorPrefix = false,
            comparisonValues: Record<string, ComparisonValue>
          ) {
            if (!widget) return '';
            switch (property) {
              case 'indicator':
                if (indicatorPrefix) {
                  let prefix = '';
                  const indicator = indicatorStore.getById(key);
                  if (!indicator) return '';
                  if (indicator.parent) {
                    prefix = indicatorStore.getById(indicator.parent).name;
                  }
                  return prefix
                    ? `${prefix}: ${indicator.name}`
                    : indicator.name;
                } else {
                  return indicatorStore.getById(key)?.name || '';
                }
              case 'feature':
                return (
                  widget.features?.[key]?.name ||
                  comparisonValues[key]?.name ||
                  ''
                );
              default:
                return String(layerStore.getById(key)?.year || '');
            }
          },
          getGroup(widget: WidgetConfig, property: string, key: string) {
            if (!widget) return null;
            switch (property) {
              case 'indicator':
                return indicatorStore.getParent(key)?.name || null;
              default:
                return null;
            }
          },
          getLegendGroups(
            widget: WidgetConfig
          ): Record<string, string[]> | null {
            if (!widget) return null;
            if (widget.groupProperty === 'indicator') {
              const sets: Record<string, string[]> = {};
              widget.indicators.forEach((id: string) => {
                const parentName = indicatorStore.getParent(id)?.name;
                if (!parentName) return;
                if (!sets[parentName]) {
                  sets[parentName] = [];
                }
                sets[parentName].push(indicatorStore.getById(id).name);
              });
              return sets;
            }
            return null;
          },
          dataSetProperty(conf: WidgetConfig) {
            const possibleProps: DataGroupProperty[] = [
              'feature',
              'indicator',
              'dataLayer',
            ];
            return possibleProps.find(
              prop => ![conf.focusProperty, conf.groupProperty].includes(prop)
            )!;
          },
          /**
           * Updates the chart data based on the provided widget configuration and dataset properties.
           * This method processes and transforms the data required for rendering charts, applying appropriate configurations
           * like grouping, sorting, color schemes, and additional feature layers as defined.
           *
           * @param {EntityId} [id] - The unique identifier of the widget to update. If not provided, the currently active widget is used.
           * @param {boolean} [rerender=true] - Specifies whether the chart should be re-rendered after updating the data. Defaults to `true`.
           * @return {null} Returns null after updating the chart's configuration and dataset properties.
           */
          updateChartData(id?: EntityId, rerender = true) {
            const selectedFeatures =
              gridStore.options()?.selectedFeatures.map(item => String(item)) ||
              null;
            let widgetById: CustomGridStackWidget | null = null;
            const keyValueComparisonValues = comparisonStore.byKey();

            if (id) {
              widgetById = get(id);
            }

            const config = widgetById ? widgetById.widget : activeWidget();
            const type = widgetById ? widgetById.type : widgetType();
            let layerIds: string[] = [];
            let dataKeys: string[] = [];

            if (!config || !type) return null;
            if (config.context !== 'local') {
              const data = this.getGroupedWegweiserData(id as string);

              if (data && data.datasets) {
                config.chartData = {
                  datasets: data.datasets,
                  labels: data.labels,
                  minValue: getMinValue(data.datasets),
                  maxValue: getMaxValue(data.datasets),
                };

                const current = widgetById || { ...selected() };
                current.widget = config;
                update(current.id as string, current, rerender);
              }
              return null;
            }

            const groupedData = indicatorDataStore.groupByProperties(
              config[config.focusProperty],
              config.focusProperty,
              config.groupProperty,
              this.dataSetProperty(config),
              {
                indicators: config.indicators,
                disabledYears: config.disabledYears,
                dataLayerType: config.dataLayerType,
                disabledDataLayers: layerStore.getIdsByYears(
                  config.dataLayerType,
                  config.disabledYears
                ),
                disabledFeatures: config.disabledFeatures,
                availableFeatureKeys: Object.keys(config.features || []),
                comparisonKeys: Object.keys(keyValueComparisonValues),
              }
            );

            const comparisonData = groupedData.comparison;
            const featureData = groupedData.features;
            const dataLayerIds = groupedData.dataLayerIds;

            let datasets: any;
            let compSets: any;

            if (helper.isAxisBasedType(type || 'adder')) {
              datasets = this.convertToAxisData(
                featureData,
                config,
                type,
                keyValueComparisonValues
              );
            } else if (config && type) {
              datasets = Object.values(featureData).map((item, idx) => {
                const labelKey = Object.keys(featureData)[idx];
                const dataSetProperty = this.dataSetProperty(config);
                const label = this.getDatasetLabel(
                  config,
                  dataSetProperty,
                  labelKey,
                  false,
                  keyValueComparisonValues
                );
                const tooltip = this.getDatasetLabel(
                  config,
                  dataSetProperty,
                  labelKey,
                  true,
                  keyValueComparisonValues
                );

                const color = helper.getChartBackgroundColor(
                  type,
                  config.colorset,
                  config.invertColors,
                  idx,
                  config.featureOpacity,
                  Object.values(item).length
                );

                layerIds = layerIds.concat(
                  config.groupProperty === 'dataLayer'
                    ? Object.keys(item)
                    : Object.keys(featureData)
                );

                const setKeys = Object.keys(item);
                dataKeys = setKeys;

                return {
                  data: Object.values(item),
                  dataKeys: Object.keys(item),
                  backgroundColor: color,
                  borderColor:
                    selectedFeatures && selectedFeatures.length
                      ? Object.keys(item).map(key => {
                          if (selectedFeatures.includes(key)) {
                            return '#000000';
                          } else {
                            return updateAlpha(
                              color,
                              config.featureOpacity
                                ? config.featureOpacity + 0.4
                                : 1
                            );
                          }
                        })
                      : updateAlpha(
                          color,
                          config.featureOpacity
                            ? config.featureOpacity + 0.4
                            : 1
                        ),
                  label: label,
                  customTooltip: tooltip,
                  labelKey: labelKey,
                  pointRadius: config.dotSize || 4,
                  borderWidth: config.borderWidth || 2,
                  tension: config.lineTension || 0,
                  group: this.getGroup(config, dataSetProperty, labelKey),
                };
              });

              if (
                type === 'bar' &&
                config.comparisonValues &&
                config.comparisonValues.length
              ) {
                datasets = datasets.map((dataset: any) => {
                  dataset.type = 'bar';
                  return dataset;
                });

                const compSets: any[] = [];
                config.comparisonValues.forEach((compKey, compIndex) => {
                  const data = comparisonData[compKey];
                  if (
                    data &&
                    (config.groupProperty === 'feature' ||
                      config.focusProperty === 'feature')
                  ) {
                    Object.values(data).forEach((item: any, idx) => {
                      const labelKey = Object.keys(featureData)[idx];
                      const color = helper.getChartBackgroundColor(
                        type,
                        config.colorset,
                        config.invertColors,
                        idx
                      );
                      compSets.push(
                        helper.getComparisonLineOptions(Object.values(item), {
                          color,
                          compIndex,
                          legendIndex: idx,
                          labelKey: labelKey,
                          tooltip: comparisonStore.getNameByKey(compKey),
                          asBar: !datasets.length,
                        })
                      );
                    });
                  } else if (data) {
                    compSets.push(
                      helper.getComparisonLineOptions(Object.values(data), {
                        color: 'red',
                        tooltip: comparisonStore.getNameByKey(compKey),
                        asBar: !datasets.length,
                      })
                    );
                  }
                });
                datasets = datasets.concat(compSets);
              }
            }

            if (datasets) {
              const labels = this.getLabelsByGroupProperty(
                config,
                dataLayerIds,
                dataKeys
              );

              const keys = this.getKeysByGroupProperty(config);
              config.chartData = sortChartGroups(
                labels as string[],
                keys as string[],
                datasets,
                config.sort.enable,
                config.sort.property,
                config.sort.direction
              );

              config.chartData.legendGroups = this.getLegendGroups(config);
              config.chartData.minValue = getMinValue(datasets);
              config.chartData.maxValue = getMaxValue(datasets);
              config.groupedData = groupedData;
              config.dataLayers = makeUniqueStringArray(layerIds);

              const current = widgetById || { ...selected() };
              current.widget = config;
              update(current.id as string, current, rerender);
            }

            return null;
          },
          calculateScatterData(
            xs: number[],
            ys: number[],
            rs: number[] | null
          ): ScatterData[] {
            let minR: number | undefined;
            let maxR: number | undefined;

            if (rs) {
              // Calculate min and max of the `r` values
              minR = Math.min(...rs);
              maxR = Math.max(...rs);
            }

            const normalize = (
              value: number,
              min: number,
              max: number,
              scaledMin: number,
              scaledMax: number
            ): number => {
              return (
                ((value - min) / (max - min)) * (scaledMax - scaledMin) +
                scaledMin
              );
            };

            return xs
              .map((x: number, i: number) => {
                const rawR = rs && rs[i] ? rs[i] : 1; // Raw value of `r`
                const scaledR =
                  rs && maxR !== undefined && minR !== undefined
                    ? normalize(rawR, minR, maxR, 1, 20) // Normalize `r` to a range of 1–20 (example range)
                    : 1; // Default radius if no `r` values provided

                return {
                  x,
                  y: ys[i],
                  r: scaledR,
                  rRaw: rs?.[i] || undefined,
                };
              })
              .filter(
                (data: ScatterData) =>
                  data.x !== undefined && data.y !== undefined
              );
          },
          convertToAxisData(
            groupedData: Record<string, Record<string, number | null>>,
            config: WidgetConfig,
            type: WidgetType,
            comparisonValues: Record<string, ComparisonValue>
          ) {
            type = type || 'adder';
            const ids = config?.axisProperties.length
              ? config.axisProperties
              : [...Object.keys(groupedData).slice(0, 2)];

            if (type === 'bubble') {
              const radiusValue = Object.keys(groupedData)[2];
              if (radiusValue) ids.push(radiusValue);
            }

            if (!config || !groupedData || !type) {
              return;
            }

            if (
              !ids ||
              (ids.length < 2 && type === 'scatter') ||
              (ids.length < 3 && type === 'bubble')
            ) {
              return;
            }

            const radiusProperty =
              config?.radiusProperty || Object.keys(groupedData)[2] || null;
            if (radiusProperty) {
              ids.push(radiusProperty);
            }

            let label = 'x: ';
            const chartData: ChartDataset = {
              data: [],
              backgroundColor: helper.getChartBackgroundColor(
                type,
                config.colorset,
                config.invertColors,
                0
              ),
              borderColor: helper.getChartBackgroundColor(
                type,
                config.colorset,
                config.invertColors,
                0
              ),
              label: '',
              pointRadius: config.dotSize || 4,
            };

            const dataItems: any = ids.map((id, idx) => {
              const dataLabel = this.getDatasetLabel(
                config,
                this.dataSetProperty(config),
                id,
                true,
                comparisonValues
              );
              if (idx === 1) {
                label += ' | y: ';
                config.xAxisLabel = config.axis?.x.label || dataLabel;
              } else if (idx === 0) {
                config.yAxisLabel = config.axis?.y.label || dataLabel;
              }

              label += dataLabel;

              return groupedData[id];
            });
            if (
              !dataItems ||
              !dataItems[0] ||
              !dataItems[1] ||
              dataItems.length < 2
            )
              return;

            const xs: number[] = Object.values(dataItems[0]);
            const ys: number[] = Object.values(dataItems[1]);
            const rs: number[] | null = dataItems[2]
              ? Object.values(dataItems[2])
              : null;

            const converted: ScatterData[] = this.calculateScatterData(
              xs,
              ys,
              rs
            ).filter(data => data.x !== undefined && data.y !== undefined);

            chartData.data = converted;
            chartData.label = label;

            if (config.displayRegression) {
              const regressionData: RegressionData =
                helper.calcRegression(converted);
              const regressionDataset = {
                regression: true,
                label: 'r2: ' + regressionData.r2,
                customTooltip: 'r2: ' + regressionData.r2,
                r2: regressionData.r2,
                data: regressionData.line,
                type: 'line',
                borderColor: 'rgba(255, 99, 132, 1)',
                borderWidth: 2,
                fill: false,
              };

              return [chartData, regressionDataset];
            } else {
              return [chartData];
            }
          },
          getChartData(id: EntityId) {
            const widget = entityMap()[id];
            if (!widget || !widget.widget) return null;

            if (widget.widget.context === 'external') {
              return widget.widget.chartData;
            } else {
              return widget.widget.chartData;
            }
          },
          getGroupedWegweiserData(widgetId: string) {
            const config = widgetId ? get(widgetId) : { ...selected() };
            if (
              !config ||
              !config.type ||
              !config.widget ||
              !config.widget.wegweiserData ||
              !config.widget.wegweiserData?.indicators.length
            )
              return { labels: [], datasets: [], groups: [] };

            const focusProperty = config.widget.focusProperty;
            const groupProperty = config.widget.groupProperty;

            switch (focusProperty) {
              case 'dataLayer':
                return groupProperty === 'indicator'
                  ? this.getWegweiserDataLayerDataGroupedByIndicators(
                      Number(config.widget.year),
                      config.type,
                      config.widget
                    )
                  : this.getWegweiserDataLayerDataGroupedByRegions(
                      Number(config.widget.year),
                      config.type,
                      config.widget
                    );
              case 'indicator':
                return groupProperty === 'feature'
                  ? this.getWegweiserIndicatorDataGroupedByRegions(
                      Number(config.widget.indicator),
                      config.type,
                      config.widget
                    )
                  : this.getWegweiserIndicatorDataGroupedByYears(
                      Number(config.widget.indicator),
                      config.type,
                      config.widget
                    );
              case 'feature':
                return groupProperty === 'indicator'
                  ? this.getWegweiserRegionDataGroupedByIndicators(
                      Number(config.widget.feature),
                      config.type,
                      config.widget
                    )
                  : this.getWegweiserRegionDataGroupedByYears(
                      Number(config.widget.feature),
                      config.type,
                      config.widget
                    );
            }
          },

          /**
           * Transforms WegweiserData into a Chart.js data format for a bar chart,
           * where indicators are on the x-axis, and regions are in the legend for a single year.
           *
           * @param year - The ID of the indicator to focus on.
           * @param type - The WidgetType
           * @param widget - The WidgetConfig
           * @returns A Chart.js compatible data object.
           */
          getWegweiserDataLayerDataGroupedByIndicators(
            year: number,
            type: WidgetType,
            widget: WidgetConfig
          ) {
            const data = widget.wegweiserData;
            if (!data) return null;
            let yearIndex = data.years.indexOf(year);
            if (yearIndex === -1) {
              yearIndex = data.years.length - 1;
            }

            const groups: any[] = [];
            // Extract indicator names (x-axis labels)
            const labels = data.indicators.map(indicator => {
              groups.push({
                label: indicator.name,
                key: String(indicator.id),
              });
              return indicator.name;
            });

            // Create datasets for each region
            const datasets = data.regions.map(
              (region: WegweiserRegion, idx: number) => {
                const color = helper.getChartBackgroundColor(
                  type,
                  widget.colorset,
                  widget.invertColors,
                  idx
                );

                return {
                  label: region.name, // Region name in legend
                  data: data.indicators.map(indicator => {
                    // Extract the value for the region and given year index
                    const regionIndex = data.regions.findIndex(
                      r => r.id === region.id
                    );
                    return (
                      indicator.regionYearValues[regionIndex]?.[yearIndex] ??
                      null
                    ); // Handle missing values
                  }),
                  dataKeys: data.indicators.map(indicator =>
                    String(indicator.id)
                  ),
                  backgroundColor: color,
                  borderColor: color,
                  pointRadius: widget.dotSize || 4,
                  borderWidth: widget.borderWidth || 2,
                  tension: widget.lineTension || 0,
                };
              }
            );

            return { labels, datasets, groups };
          },

          /**
           * Transforms WegweiserData into a Chart.js data format for a bar chart,
           * where regions are on the x-axis, and indicators are in the legend for a single year.
           *
           * @param year - The ID of the indicator to focus on.
           * @param type - The WidgetType
           * @param widget - The WidgetConfig
           *
           * @returns A Chart.js compatible data object.
           */
          getWegweiserDataLayerDataGroupedByRegions(
            year: number,
            type: WidgetType,
            widget: WidgetConfig
          ) {
            // Find the index of the given year
            const data = widget.wegweiserData;
            if (!data) return null;

            let yearIndex = data.years.indexOf(year);
            if (yearIndex === -1) {
              yearIndex = data.years.length - 1;
            }

            // X-Axis Labels (Regions)
            const groups: any[] = [];
            const labels = data.regions.map(region => {
              groups.push({
                label: region.name,
                key: String(region.id),
              });
              return region.name;
            });

            // Create datasets for each indicator
            const datasets = data.indicators.map(
              (indicator: WegweiserIndicator, idx: number) => {
                const color = helper.getChartBackgroundColor(
                  type,
                  widget.colorset,
                  widget.invertColors,
                  idx
                );

                return {
                  label: indicator.name, // Indicator name in legend
                  data: data.regions.map(region => {
                    // Extract the value for this region and the given year index
                    const regionIndex = data.regions.findIndex(
                      r => r.id === region.id
                    );
                    return (
                      indicator.regionYearValues[regionIndex]?.[yearIndex] ??
                      null
                    ); // Handle missing values
                  }),
                  dataKeys: data.regions.map(region => String(region.id)),
                  labelKey: String(indicator.id),
                  backgroundColor: color,
                  borderColor: color,
                  pointRadius: widget.dotSize || 4,
                  borderWidth: widget.borderWidth || 2,
                  tension: widget.lineTension || 0,
                };
              }
            );

            return { labels, datasets, groups };
          },

          /**
           * Transforms WegweiserData into a Chart.js data format for a bar chart,
           * where years  are on the x-axis, and indicators are in the legend for a single region.
           *
           * @param regionId - The ID of the indicator to focus on.
           * @param type - The WidgetType
           * @param widget - The WidgetConfig
           *
           * @returns A Chart.js compatible data object.
           */
          getWegweiserRegionDataGroupedByYears(
            regionId: number,
            type: WidgetType,
            widget: WidgetConfig
          ) {
            const data = widget.wegweiserData;
            if (!data) return null;
            // Find the index of the selected region
            const regionIndex = data.regions.findIndex(
              region => region.id === regionId
            );
            if (regionIndex === -1) {
              throw new Error(`Region with ID ${regionId} not found.`);
            }

            const groups: any[] = [];
            const disabledYears = widget.disabledYears || [];
            const disabledYearsIdx: number[] = disabledYears.map(disabledYear =>
              data.years.indexOf(disabledYear)
            );
            const availableYears = data.years.filter(
              year => !disabledYears.includes(year)
            );
            // X-Axis Labels (Years)
            const labels = availableYears.map(year => {
              groups.push({
                label: String(year),
                key: year,
              });
              return String(year);
            });

            // Create datasets for each indicator
            const datasets = data.indicators.map(
              (indicator: WegweiserIndicator, idx: number) => {
                const color = helper.getChartBackgroundColor(
                  type,
                  widget.colorset,
                  widget.invertColors,
                  idx
                );
                return {
                  label: indicator.name, // Indicator name in legend
                  labelKey: String(indicator.id),
                  data:
                    indicator.regionYearValues[regionIndex].filter(
                      (val, idx) => !disabledYearsIdx.includes(idx)
                    ) ?? [], // Get values for this region
                  dataKeys: labels,
                  backgroundColor: color,
                  borderColor: color,
                  pointRadius: widget.dotSize || 4,
                  borderWidth: widget.borderWidth || 2,
                  tension: widget.lineTension || 0,
                };
              }
            );

            return { labels, datasets, groups };
          },

          /**
           * Transforms WegweiserData into a Chart.js data format for a bar chart,
           * where indicators are on the x-axis, and years are in the legend for a single region.
           *
           * @param regionId - The ID of the indicator to focus on.
           * @param type - The WidgetType
           * @param widget - The WidgetConfig
           * @returns A Chart.js compatible data object.
           */
          getWegweiserRegionDataGroupedByIndicators(
            regionId: number,
            type: WidgetType,
            widget: WidgetConfig
          ) {
            const data = widget.wegweiserData;
            if (!data) return null;
            const regionIndex = data.regions.findIndex(
              region => region.id === regionId
            );
            if (regionIndex === -1) {
              throw new Error(`Region with ID ${regionId} not found.`);
            }

            const groups: any[] = [];
            // X-Axis Labels (Indicators)
            const labels = data.indicators.map(indicator => {
              groups.push({
                label: indicator.name,
                key: String(indicator.id),
              });
              return indicator.name;
            });

            // Create datasets for each year
            let bgIdx = -1;
            const disabledYears = widget.disabledYears || [];
            const availableYears = data.years.filter(
              year => !disabledYears.includes(year)
            );
            const datasets = availableYears
              .map((year, yearIndex) => {
                bgIdx++;
                const color = helper.getChartBackgroundColor(
                  type,
                  widget.colorset,
                  widget.invertColors,
                  bgIdx
                );
                return {
                  label: `${year}`, // Year name in legend
                  labelKey: String(year),
                  year: year,
                  data: data.indicators.map(indicator => {
                    return (
                      indicator.regionYearValues[regionIndex]?.[yearIndex] ??
                      null
                    ); // Get value for the year
                  }),
                  dataKeys: data.indicators.map(indicator =>
                    String(indicator.id)
                  ),
                  backgroundColor: color,
                  borderColor: color,
                  pointRadius: widget.dotSize || 4,
                  borderWidth: widget.borderWidth || 2,
                  tension: widget.lineTension || 0,
                };
              })
              .filter(dataItem => dataItem !== null);

            return { labels, datasets, groups };
          },
          /**
           * Transforms WegweiserData into a Chart.js data format for a bar chart,
           * where years are on the x-axis, and regions are in the legend for a single indicator.
           *
           * @param indicatorId - The ID of the indicator to focus on.
           * @param type - The WidgetType
           * @param widget - The WidgetConfig
           * @returns A Chart.js compatible data object.
           */
          getWegweiserIndicatorDataGroupedByYears(
            indicatorId: number,
            type: WidgetType,
            widget: WidgetConfig
          ) {
            // Find the selected indicator
            const data = widget.wegweiserData;
            if (!data) return null;
            const indicator = data.indicators.find(
              ind => ind.id === indicatorId
            );
            if (!indicator) {
              throw new Error(`Indicator with ID ${indicatorId} not found.`);
            }

            const groups: any[] = [];
            const disabledYears = widget.disabledYears || [];
            const disabledYearsIdx: number[] = disabledYears.map(disabledYear =>
              data.years.indexOf(disabledYear)
            );
            const availableYears = data.years.filter(
              year => !disabledYears.includes(year)
            );
            // X-Axis Labels (Years)
            const labels = availableYears.map(year => {
              groups.push({
                label: String(year),
                key: String(year),
              });

              return String(year);
            });

            // Create datasets for each region
            const datasets = data.regions.map((region, regionIndex) => {
              const color = helper.getChartBackgroundColor(
                type,
                widget.colorset,
                widget.invertColors,
                regionIndex
              );
              return {
                label: region.name, // Region name in legend
                labelKey: String(region.id), // Region name in legend
                data:
                  indicator.regionYearValues[regionIndex].filter(
                    (val, idx) => !disabledYearsIdx.includes(idx)
                  ) ?? [], // Get values for this region
                dataKeys: labels,
                backgroundColor: color,
                borderColor: color,
                pointRadius: widget.dotSize || 4,
                borderWidth: widget.borderWidth || 2,
                tension: widget.lineTension || 0,
              };
            });

            return { labels, datasets, groups };
          },
          /**
           * Transforms WegweiserData into a Chart.js data format for a bar chart,
           * where regions are on the x-axis, and years are in the legend for a single indicator.
           *
           * @param indicatorId - The ID of the indicator to focus on.
           * @param type - The WidgetType
           * @param widget - The WidgetConfig
           * @returns A Chart.js compatible data object.
           */
          getWegweiserIndicatorDataGroupedByRegions(
            indicatorId: number,
            type: WidgetType,
            widget: WidgetConfig
          ) {
            const data = widget.wegweiserData;
            if (!data) return null;
            // Find the selected indicator
            const indicator = data.indicators.find(
              ind => ind.id === indicatorId
            );
            if (!indicator) {
              throw new Error(`Indicator with ID ${indicatorId} not found.`);
            }

            const groups: any[] = [];
            // X-Axis Labels (Regions)
            const labels = data.regions.map(region => {
              groups.push({
                label: region.name,
                key: String(region.id),
              });
              return region.name;
            });
            const disabledYears = widget.disabledYears || [];
            const availableYears = data.years.filter(
              year => !disabledYears.includes(year)
            );
            // Create datasets for each year
            let bgIdx = -1;
            const datasets = availableYears
              .map((year, yearIndex) => {
                bgIdx++;
                const color = helper.getChartBackgroundColor(
                  type,
                  widget.colorset,
                  widget.invertColors,
                  bgIdx
                );

                return {
                  label: `${year}`, // Year name in legend
                  labelKey: `${year}`,
                  year: year,
                  data: data.regions.map((_, regionIndex) => {
                    return (
                      indicator.regionYearValues[regionIndex]?.[yearIndex] ??
                      null
                    ); // Get value for this region & year
                  }),
                  dataKeys: data.regions.map(region => String(region.id)),
                  backgroundColor: color,
                  borderColor: color,
                  pointRadius: widget.dotSize || 4,
                  borderWidth: widget.borderWidth || 2,
                  tension: widget.lineTension || 0,
                };
              })
              .filter(dataItem => dataItem !== null);

            return { labels, datasets, groups };
          },
        };

        return chartMethods;
      }
    )
  );
}
